import type { CustomerContextSummary } from "../../shared/customer-record-schemas";
import type { OrganizationOverview } from "../../shared/organization-overview-schemas";

type Contact = CustomerContextSummary["people"][number];
type Member = OrganizationOverview["members"][number];
type Invitation = OrganizationOverview["pendingInvitations"][number];
type Variant = "success" | "warning" | "destructive" | "secondary";

/** One person, whether a contact we keep, a workspace member, or both (ADR-0088). */
export type PersonRow = {
  key: string;
  name: string;
  email: string | null;
  contact: Contact | null;
  member: Member | null;
  invitation: Invitation | null;
  primary: boolean;
  role: string;
  roleDetail: string | null;
  tools: string;
  status: { label: string; variant: Variant };
};

export const ROLE_LABELS = {
  platform_admin: "Platform administrator",
  organization_admin: "Administrator",
  member: "Member",
} as const;

export const ROLE_HINTS = {
  platform_admin: "Our own staff account",
  organization_admin: "Manages people and approves support access",
  member: "Uses the tools they are given",
} as const;

function memberStatus(member: Member): PersonRow["status"] {
  if (member.accountStatus === "disabled") return { label: "Disabled", variant: "destructive" };
  if (member.membershipStatus !== "active") return { label: "Suspended", variant: "destructive" };
  return { label: "Active", variant: "success" };
}

function statusOf(row: Omit<PersonRow, "status">): PersonRow["status"] {
  if (row.member) return memberStatus(row.member);
  if (row.invitation)
    return row.invitation.state === "expired"
      ? { label: "Invitation expired", variant: "warning" }
      : { label: "Invited", variant: "warning" };
  return row.contact?.status === "inactive"
    ? { label: "Inactive", variant: "secondary" }
    : { label: "No sign-in", variant: "secondary" };
}

function roleOf(member: Member | null, invitation: Invitation | null) {
  if (member) return ROLE_LABELS[member.role];
  if (invitation) return ROLE_LABELS[invitation.role];
  return "Contact only";
}

/** Contacts, members, and invitations merged by email address, primary contact first. */
export function peopleRows(input: {
  contacts: readonly Contact[];
  members: readonly Member[];
  invitations: readonly Invitation[];
  primaryId: string | null;
}): PersonRow[] {
  const byEmail = new Map<string, Omit<PersonRow, "status">>();
  const rows: Omit<PersonRow, "status">[] = [];
  const place = (email: string | null, create: () => Omit<PersonRow, "status">) => {
    const existing = email ? byEmail.get(email.toLowerCase()) : undefined;
    if (existing) return existing;
    const row = create();
    rows.push(row);
    if (email) byEmail.set(email.toLowerCase(), row);
    return row;
  };
  // Keyed by email when there is one, so a row keeps its identity when a contact record is added.
  const blank = (key: string, name: string, email: string | null) => ({
    key: email ? `email:${email.toLowerCase()}` : key,
    name,
    email,
    contact: null,
    member: null,
    invitation: null,
    primary: false,
    role: "Contact only",
    roleDetail: null,
    tools: "—",
  });
  for (const contact of input.contacts) {
    const row = place(contact.email, () =>
      blank(`contact:${contact.id}`, contact.displayName, contact.email),
    );
    row.contact = contact;
    row.primary = contact.id === input.primaryId;
    row.roleDetail = contact.title;
  }
  for (const member of input.members) {
    const row = place(member.email, () =>
      blank(`member:${member.userId}`, member.displayName, member.email),
    );
    row.member = member;
    row.tools = `${member.toolAccessCount} ${member.toolAccessCount === 1 ? "tool" : "tools"}`;
  }
  for (const invitation of input.invitations) {
    const row = place(invitation.email, () =>
      blank(`invitation:${invitation.id}`, invitation.displayName, invitation.email),
    );
    if (!row.member) row.invitation = invitation;
  }
  return rows
    .map((row) => ({ ...row, role: roleOf(row.member, row.invitation), status: statusOf(row) }))
    .sort((a, b) => Number(b.primary) - Number(a.primary) || a.name.localeCompare(b.name));
}
